import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { faqService } from '../api';
import { Button, ThemedText, Collapsible, Pagination, ActionMenu, ConfirmDialog, ErrorState } from '../components';
import { usePagination } from '../hooks/usePagination';
import { StatusPalette, withOpacity } from '../constants/statusColors';
import { FAQ_CATEGORIES, getCategoryIcon } from '../constants/faqCategories';
import { PageContainer, PageHeaderRow as PageHeader } from '../components/PageStyles';
import { StyledSearchContainer, SearchIcon, TransparentInput } from '../components/SearchStyles';
import { ToggleSwitch, ToggleInput, ToggleSlider } from '../components/ToggleStyles';
import { Plus, Search, Globe, Building2 } from 'lucide-react';

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['p','br','strong','b','em','i','u','s','a','img','ul','ol','li','blockquote','code','pre','h1','h2','h3','h4','h5','h6','span','div'],
  ALLOWED_ATTR: ['href','src','alt','style','target','rel','class','width','height'],
};

const sanitizeHtml = (html) => DOMPurify.sanitize(html || '', SANITIZE_CONFIG);

const stripHtmlTags = (html) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html || '';
  return (tmp.textContent || tmp.innerText || '').trim();
};

const SearchContainer = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
  display: flex;
  gap: ${props => props.theme.spacing.lg};
  align-items: center;
  flex-wrap: wrap;
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  width: 100%;
`;

const FilterRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const FilterLabel = styled.span`
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  color: ${props => props.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FilterButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background-color: ${props => props.$active ? props.theme.colors.tint : props.theme.colors.background};
  color: ${props => props.$active ? 'white' : props.theme.colors.text};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${props => props.theme.typography.sizes.small};
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background-color: ${props => props.$active ? props.theme.colors.tint : props.theme.colors.backgroundSecondary};
  }
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const FAQCard = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  transition: box-shadow 0.2s ease;
  border-left: ${props => props.$isSystem ? `3px solid ${props.theme.colors.tint}` : 'none'};

  &:hover {
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

const FAQCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const FAQCardTitle = styled.div`
  flex: 1;
`;

const FAQCardMeta = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
  align-items: center;
`;

const MetaBadge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: ${props => {
    if (props.$type === 'category') return props.theme.colors.tint + '20';
    if (props.$type === 'status') return props.$isActive ? withOpacity(StatusPalette.green) : withOpacity(StatusPalette.red);
    if (props.$type === 'visibility') return props.$isPublic ? withOpacity(StatusPalette.blue) : withOpacity(StatusPalette.amber);
    if (props.$type === 'source') return props.$isSystem ? '#8b5cf620' : withOpacity(StatusPalette.green);
    return props.theme.colors.muted + '20';
  }};
  color: ${props => {
    if (props.$type === 'category') return props.theme.colors.tint;
    if (props.$type === 'status') return props.$isActive ? StatusPalette.green : StatusPalette.red;
    if (props.$type === 'visibility') return props.$isPublic ? StatusPalette.blue : StatusPalette.amber;
    if (props.$type === 'source') return props.$isSystem ? '#8b5cf6' : StatusPalette.green;
    return props.theme.colors.text;
  }};
`;

const FAQCardActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  flex-shrink: 0;
`;

const RichTextContent = styled.div`
  color: ${props => props.theme.colors.text};
  line-height: 1.6;
  font-size: ${props => props.theme.typography.sizes.default};

  h1, h2, h3 { color: ${props => props.theme.colors.text}; margin: ${props => props.theme.spacing.md} 0 ${props => props.theme.spacing.sm}; }
  p { margin: ${props => props.theme.spacing.sm} 0; }
  ul, ol { padding-left: ${props => props.theme.spacing.xl}; }
  code { background: ${props => props.theme.colors.background}; padding: 2px 6px; border-radius: 4px; font-size: 0.9em; }
  pre { background: ${props => props.theme.colors.background}; padding: ${props => props.theme.spacing.md}; border-radius: ${props => props.theme.borderRadius.md}; overflow-x: auto; }
  a { color: ${props => props.theme.colors.tint}; }
  img { max-width: 100%; border-radius: ${props => props.theme.borderRadius.md}; }
  blockquote { border-left: 3px solid ${props => props.theme.colors.border}; padding-left: ${props => props.theme.spacing.md}; color: ${props => props.theme.colors.muted}; margin: ${props => props.theme.spacing.sm} 0; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.muted};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.muted};
`;

export const FAQsSection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isSuperAdmin } = useRole();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [showInactive, setShowInactive] = useState(false);

  const {
    currentPage,
    totalCount,
    totalPages,
    pageSize,
    startIndex,
    endIndex,
    goToPage,
    resetPage,
    setTotalCount,
    paginationParams,
    extractPagedData,
  } = usePagination(20);

  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  useEffect(() => {
    fetchFaqs();
  }, [showInactive, currentPage]);

  // Reset to page 1 on filter change
  useEffect(() => {
    resetPage();
  }, [showInactive, searchTerm, categoryFilter, sourceFilter]);

  const fetchFaqs = async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const response = await faqService.getAllFaqs({ includeInactive: showInactive, ...paginationParams });
      const { items, totalCount: total } = extractPagedData(response);
      setFaqs(items);
      setTotalCount(total);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
      setFetchError(error?.response?.data?.message || 'Failed to load FAQs. Please check your connection and try again.');
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFaq = async (id) => {
    try {
      await faqService.deleteFaq(id);
      toast.success('FAQ deleted successfully');
      setDeleteConfirmId(null);
      fetchFaqs();
    } catch (error) {
      console.error('Failed to delete FAQ:', error);
      toast.error(error.response?.data?.message || 'Failed to delete FAQ');
    }
  };

  const canEditFaq = (faq) => {
    if (isSuperAdmin) return true;
    // OrgAdmin can only edit org-specific FAQs (not system FAQs)
    return faq.organizationId !== null && faq.organizationId !== undefined;
  };

  // Filter FAQs
  const filteredFaqs = faqs.filter(faq => {
    const plainAnswer = stripHtmlTags(faq.answer);
    const term = searchTerm.toLowerCase();
    const matchesSearch = faq.question.toLowerCase().includes(term) ||
      plainAnswer.toLowerCase().includes(term);
    const matchesCategory = categoryFilter === 'all' || (faq.category || 'General') === categoryFilter;
    const matchesSource = sourceFilter === 'all' ||
      (sourceFilter === 'system' && (faq.organizationId === null || faq.organizationId === undefined)) ||
      (sourceFilter === 'organization' && faq.organizationId !== null && faq.organizationId !== undefined);
    return matchesSearch && matchesCategory && matchesSource;
  });

  // Get unique categories from loaded FAQs + predefined
  const predefinedValues = FAQ_CATEGORIES.map(c => c.value);
  const faqCategories = faqs.map(f => f.category || 'General');
  const allCategories = [...new Set([...predefinedValues, ...faqCategories])];

  return (
    <>
      <PageContainer theme={theme}>
        <PageHeader theme={theme}>
          <ThemedText type="title">Manage FAQs</ThemedText>
          <Button onClick={() => navigate('/faqs/new')}>
            <Plus size={14} /> Create FAQ
          </Button>
        </PageHeader>

        <SearchContainer theme={theme}>
          <StyledSearchContainer>
            <SearchIcon><Search size={16} /></SearchIcon>
            <TransparentInput
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              theme={theme}
            />
          </StyledSearchContainer>

          <ToggleSwitch theme={theme}>
            <ToggleInput
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <ToggleSlider theme={theme} $checked={showInactive} />
            <ThemedText type="defaultSemiBold">Show inactive FAQs</ThemedText>
          </ToggleSwitch>
        </SearchContainer>

        {/* Source filter */}
        <FilterRow theme={theme}>
          <FilterLabel theme={theme}>Source</FilterLabel>
          <FilterButtons theme={theme}>
            <FilterButton theme={theme} $active={sourceFilter === 'all'} onClick={() => setSourceFilter('all')}>
              All
            </FilterButton>
            <FilterButton theme={theme} $active={sourceFilter === 'system'} onClick={() => setSourceFilter('system')}>
              <Globe size={13} /> System
            </FilterButton>
            <FilterButton theme={theme} $active={sourceFilter === 'organization'} onClick={() => setSourceFilter('organization')}>
              <Building2 size={13} /> Organization
            </FilterButton>
          </FilterButtons>
        </FilterRow>

        {/* Category filter */}
        <FilterRow theme={theme}>
          <FilterLabel theme={theme}>Category</FilterLabel>
          <FilterButtons theme={theme}>
            <FilterButton
              theme={theme}
              $active={categoryFilter === 'all'}
              onClick={() => setCategoryFilter('all')}
            >
              All Categories
            </FilterButton>
            {allCategories.map(cat => {
              const CatIcon = getCategoryIcon(cat);
              return (
                <FilterButton
                  key={cat}
                  theme={theme}
                  $active={categoryFilter === cat}
                  onClick={() => setCategoryFilter(cat)}
                >
                  <CatIcon size={13} /> {cat}
                </FilterButton>
              );
            })}
          </FilterButtons>
        </FilterRow>

        {loading ? (
          <LoadingState theme={theme}>
            <ThemedText>Loading FAQs...</ThemedText>
          </LoadingState>
        ) : fetchError ? (
          <ErrorState
            theme={theme}
            title="Failed to load FAQs"
            message={fetchError}
            onRetry={fetchFaqs}
          />
        ) : filteredFaqs.length === 0 ? (
          <EmptyState theme={theme}>
            <ThemedText>
              {searchTerm || categoryFilter !== 'all' || sourceFilter !== 'all'
                ? 'No FAQs found matching your criteria.'
                : 'No FAQs yet. Create your first FAQ to get started.'}
            </ThemedText>
          </EmptyState>
        ) : (
          <FAQList theme={theme}>
            {filteredFaqs.map(faq => {
              const isSystem = faq.organizationId === null || faq.organizationId === undefined;
              const CatIcon = getCategoryIcon(faq.category || 'General', faq.categoryIcon);
              return (
                <FAQCard key={faq.id} theme={theme} $isSystem={isSystem}>
                  <FAQCardHeader theme={theme}>
                    <FAQCardTitle>
                      <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.xs }}>
                        {faq.question}
                      </ThemedText>
                      <FAQCardMeta theme={theme}>
                        <MetaBadge theme={theme} $type="category">
                          <CatIcon size={12} />
                          {faq.category || 'General'}
                        </MetaBadge>
                        <MetaBadge theme={theme} $type="source" $isSystem={isSystem}>
                          {isSystem ? <><Globe size={12} /> System</> : <><Building2 size={12} /> Organization</>}
                        </MetaBadge>
                        <MetaBadge theme={theme} $type="status" $isActive={faq.isActive}>
                          {faq.isActive ? 'Active' : 'Inactive'}
                        </MetaBadge>
                        <MetaBadge theme={theme} $type="visibility" $isPublic={faq.isPublic}>
                          {faq.isPublic ? 'Public' : 'Private'}
                        </MetaBadge>
                        <MetaBadge theme={theme} $type="order">
                          Order: {faq.displayOrder}
                        </MetaBadge>
                      </FAQCardMeta>
                    </FAQCardTitle>
                    {canEditFaq(faq) && (
                      <FAQCardActions theme={theme}>
                        <ActionMenu actions={[
                          { label: 'Edit', onClick: () => navigate(`/faqs/${faq.id}/edit`) },
                          { label: 'Delete', onClick: () => setDeleteConfirmId(faq.id), variant: 'danger' },
                        ]} />
                      </FAQCardActions>
                    )}
                  </FAQCardHeader>
                  <Collapsible title="View Answer">
                    <RichTextContent
                      theme={theme}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(faq.answer) }}
                    />
                  </Collapsible>
                </FAQCard>
              );
            })}
          </FAQList>
        )}

        {/* Pagination */}
        {!loading && faqs.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            startIndex={startIndex}
            endIndex={endIndex}
            onPageChange={goToPage}
            itemLabel="FAQs"
          />
        )}

        <ConfirmDialog
          isOpen={deleteConfirmId !== null}
          title="Delete FAQ"
          message="Are you sure you want to delete this FAQ? This action cannot be undone."
          onConfirm={() => handleDeleteFaq(deleteConfirmId)}
          onCancel={() => setDeleteConfirmId(null)}
        />
      </PageContainer>
    </>
  );
};

export const FAQsPage = () => (
  <Layout currentPath="/faqs">
    <FAQsSection />
  </Layout>
);
